/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any */
import 'dayjs/locale/lt';

import IconConfig from '@assets/icons/config.svg?react';
import {
 Autocomplete,
 Box,
 FormControl,
 IconButton,
 InputLabel,
 MenuItem,
 Select,
 TextField,
 Typography,
 useTheme,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
// eslint-disable-next-line unused-imports/no-unused-imports
import isoWeek from 'dayjs/plugin/isoWeek';
import { MouseEvent, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ColPresetType } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { StructureType } from '../../../types/types';
import { findStructure, getTranslation } from '../../../utils/dataTypes';
import { isWeightModelField } from '@/utils/structureUtils';
import SelectApi from '../SelectApi';
import DateRangeFilter from './DateRangeFilter';
import FilterColumnsPopover from './FilterColumnsPopover';
import OperatorMenu from './OperatorMenu';
// for start/end of week
dayjs.extend(isoWeek);

interface FilterFieldsProps {
 columns: GridColDef[];
 currentColumnsPreset: ColPresetType | null;
 structure: StructureType[];
}

const FilterFields = ({
 columns,
 currentColumnsPreset,
 structure,
}: FilterFieldsProps) => {
 const { t } = useTranslation();
 const { control, formState, setValue } = useFormContext();
 const th = useTheme();
 const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

 const noFilters = !Object.keys(currentColumnsPreset?.filters?.fields || {})
  .length;

 return (
  columns && (
   <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="lt">
    <Box
     // noValidate
     sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
     }}
    >
     <Box sx={{ overflowY: 'auto', pb: 1, flex: 1 }}>
      <Box
       sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
       }}
      >
       <Typography
        sx={{
         color: th.palette.kemek.darkGray,
         fontSize: 14,
         fontWeight: 500,
         lineHeight: 'initial',
        }}
       >
        {t('global.chosenFilters')}
       </Typography>
       <IconButton
        onClick={(e: MouseEvent<HTMLElement>) =>
         setPopoverAnchor(e.currentTarget)
        }
        sx={{ width: '24px', height: '24px', p: 0 }}
        title={t('global.chosenFilters')}
        aria-label={t('global.chosenFilters')}
        tabIndex={0}
       >
        <IconConfig
         style={{
          width: 18,
          height: 18,
          color: th.palette.kemek.darkestGray,
         }}
        />
       </IconButton>
       <FilterColumnsPopover
        anchorEl={popoverAnchor}
        onClose={() => setPopoverAnchor(null)}
        currentColumnsPreset={currentColumnsPreset}
        structure={structure}
       />
      </Box>
      <Box
       sx={{
        flex: 1,
        // overflowY: 'auto',
        pt: '12px', // for not overflowing with label
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
       }}
      >
       {noFilters && (
        <Box
         sx={{
          textAlign: 'center',
          p: '12px',
          backgroundColor: th.palette.kemek.lightBlue,
          mb: 3,
         }}
        >
         <Typography variant="body2" color="text.secondary">
          {t('global.noItemsFound')}
         </Typography>
        </Box>
       )}
       {(currentColumnsPreset?.columns || [])
        .map((col) => col.field)
        .filter((field) =>
         currentColumnsPreset?.filters?.fields?.hasOwnProperty(field),
        )
        .map((fieldKey: string) => {
         const exists = true; // columnExists(columns, fieldKey);
         const currentStructure = findStructure(structure, fieldKey);
         if (
          [
           'DateFrom',
           'DateTo',
           'DateField',
           'period',
           'year',
           'month',
           'week',
          ].includes(fieldKey) ||
          !currentStructure
         )
          return null;

         let returnObj;

         if (isWeightModelField(currentStructure)) {
          returnObj = (
           <Controller
            name={fieldKey}
            control={control}
            render={({ field }) => (
             <TextField
              {...field}
              value={field.value || ''}
              onChange={(e) => {
               const onlyNums = e.target.value.replace(/[^0-9]/g, '');
               setValue(fieldKey, onlyNums, {
                shouldDirty: true,
                shouldTouch: true,
               });
              }}
              slotProps={{
               input: {
                inputMode: 'numeric',
               },
              }}
              type="number"
              label={getTranslation(structure, fieldKey)}
              variant="outlined"
              error={!!formState?.errors?.[fieldKey]}
              fullWidth
             />
            )}
           />
          );
         } else
          switch (currentStructure?.Type) {
           case 'model':
            returnObj = (
             <SelectApi
              name={fieldKey}
              url={currentStructure.ForeignKeyUrl}
              filter={currentStructure.ForeignKeyDefaultFilter}
              label={getTranslation(structure, fieldKey)}
              isEditable
              operator="contains"
              control={control}
              setValue={setValue}
              multiple
             />
            );
            break;
           case 'array':
            returnObj = (
             <SelectApi
              name={fieldKey}
              url={currentStructure.ForeignKeyUrl}
              filter={currentStructure.ForeignKeyDefaultFilter}
              label={getTranslation(structure, fieldKey)}
              isEditable
              operator="contains"
              control={control}
              setValue={setValue}
              multiple
             />
            );
            break;
           case 'enum':
            returnObj = (
             <Controller
              name={fieldKey}
              control={control}
              defaultValue="" // Default value for controlled input
              render={({ field }) => (
               <Autocomplete
                {...field}
                multiple
                value={field.value || []}
                onChange={(_, value: any) => {
                 console.log('onChange', value, _);
                 setValue(fieldKey, value || '', {
                  shouldDirty: true,
                  shouldTouch: true,
                 });
                }}
                options={Object.entries(currentStructure.EnumValues).map(
                 ([value, label]) => ({
                  Id: value,
                  Name: label,
                 }),
                )}
                renderOption={(props, option) => (
                 <li
                  {...props}
                  key={`${fieldKey}-${option[fieldKey]}-${option.Id}`}
                 >
                  {option.Name}
                 </li>
                )}
                getOptionLabel={(option: any) =>
                 (currentStructure.EnumValues[option] || option.Name).toString()
                }
                isOptionEqualToValue={(option, value) =>
                 (option as any).Id === value.Id
                }
                renderInput={(params) => (
                 <TextField
                  {...params}
                  label={currentStructure.Translation}
                  variant="outlined"
                 />
                )}
                sx={{ width: '100%' }}
               />
              )}
             />
            );
            break;
           case 'datetime':
            returnObj = (
             <DateRangeFilter
              fieldKey={fieldKey}
              label={getTranslation(structure, fieldKey)}
             />
            );
            break;
           case 'boolean':
            returnObj = (
             <FormControl sx={{ width: '100%' }}>
              <InputLabel htmlFor={fieldKey}>
               {getTranslation(structure, fieldKey)}
              </InputLabel>
              <Controller
               name={fieldKey}
               control={control}
               defaultValue=""
               render={({ field }) => (
                <Select
                 {...field}
                 id={fieldKey}
                 value={field.value ?? ''}
                 label={getTranslation(structure, fieldKey)}
                 onChange={(e) => {
                  setValue(fieldKey, e.target.value ?? '', {
                   shouldDirty: true,
                   shouldTouch: true,
                  });
                 }}
                >
                 <MenuItem value="true">{t('global.yes')}</MenuItem>
                 <MenuItem value="false">{t('global.no')}</MenuItem>
                </Select>
               )}
              />
             </FormControl>
            );
            break;
           default:
            returnObj = (
             <Controller
              name={fieldKey}
              control={control}
              render={({ field }) => (
               <TextField
                {...field}
                value={field.value || ''}
                onChange={(e) => {
                 setValue(fieldKey, e.target.value || '', {
                  shouldDirty: true,
                  shouldTouch: true,
                 });
                }}
                label={getTranslation(structure, fieldKey)}
                variant="outlined"
                error={!!formState?.errors?.[fieldKey]}
                fullWidth
               />
              )}
             />
            );
            break;
          }

         return returnObj ? (
          <Box
           sx={{
            p: 0, // prevents overflow scroll for some reason, fix
            width: '100%',
            display: 'flex',
            gap: 1,
            alignItems: 'center',
           }}
           key={fieldKey}
          >
           <Box sx={{ flex: 1, maxWidth: { xs: '100%', md: 288 } }}>
            {returnObj}
           </Box>
           <Controller
            name={`operators[${fieldKey}]`}
            control={control}
            defaultValue=""
            //  rules={{ required: !exists ? false : 'Operator is required' }}
            render={({ field }) => (
             <>
              <TextField
               {...field}
               type="hidden"
               variant="standard"
               sx={{ display: 'none' }}
              />
              <OperatorMenu
               operators={[
                ...(columns.find((item) => item.field === fieldKey)
                 ?.filterOperators || []),
               ]}
               filter={fieldKey}
               columnExists={exists}
               value={field.value}
               structure={currentStructure}
              />
             </>
            )}
           />
          </Box>
         ) : null;
        })}
      </Box>
     </Box>
    </Box>
   </LocalizationProvider>
  )
 );
};
FilterFields.displayName = 'FilterFields';
export default FilterFields;

